import React, { useState, useEffect } from 'react';
import { 
  Card, 
  List, 
  Typography, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Empty, 
  Dropdown, 
  Tooltip, 
  Modal,
  Row,
  Col,
  DatePicker,
  Select,
  message
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MessageOutlined,
  SearchOutlined,
  DeleteOutlined,
  MoreOutlined,
  CalendarOutlined,
  FilterOutlined,
  ExportOutlined,
  StarOutlined,
  StarFilled
} from '@ant-design/icons';
import styled from 'styled-components';
import { chatGPTTheme } from '../../assets/styles/theme';
import dayjs from 'dayjs';
import { useChatHistoryStorage } from '../../shared/hooks/useChatHistoryStorage';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleLoginButton } from '../../components';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { RangePicker } = DatePicker;

// 历史记录数据类型
interface ChatHistory {
  id: string;
  title: string;
  summary: string;
  createdAt: string;
  messageCount: number;
  category: string;
  isFavorite: boolean;
  status: 'completed' | 'analyzing' | 'pending';
}

// Styled Components
const HistoryContainer = styled.div`
  min-height: 100vh;
  background: ${chatGPTTheme.colors.background};
  padding: ${chatGPTTheme.spacing.xl};
`;

const HeaderSection = styled.div`
  max-width: 1200px;
  margin: 0 auto ${chatGPTTheme.spacing.xl};
`;

const StyledTitle = styled(Title)`
  &.ant-typography h1 {
    color: ${chatGPTTheme.colors.text};
    margin-bottom: ${chatGPTTheme.spacing.md};
  }
`;

const FilterSection = styled.div`
  background: ${chatGPTTheme.colors.backgroundSecondary};
  padding: ${chatGPTTheme.spacing.lg};
  border-radius: ${chatGPTTheme.borderRadius.lg};
  margin-bottom: ${chatGPTTheme.spacing.lg};
`;

const HistoryCard = styled(Card)`
  &.ant-card {
    margin-bottom: ${chatGPTTheme.spacing.md};
    border-radius: ${chatGPTTheme.borderRadius.lg};
    transition: all 0.3s ease;
    cursor: pointer;
    
    &:hover {
      box-shadow: ${chatGPTTheme.shadows.md};
      border-color: ${chatGPTTheme.colors.primary};
    }
    
    .ant-card-body {
      padding: ${chatGPTTheme.spacing.lg};
    }
  }
`;

const StatusTag = styled(Tag)<{ $status: string }>`
  &.ant-tag {
    border-radius: ${chatGPTTheme.borderRadius.md};
    font-weight: 500;
    
    ${props => {
      switch (props.$status) {
        case 'completed':
          return `
            background: ${chatGPTTheme.colors.success}15;
            color: ${chatGPTTheme.colors.success};
            border-color: ${chatGPTTheme.colors.success}30;
          `;
        case 'analyzing':
          return `
            background: ${chatGPTTheme.colors.warning}15;
            color: ${chatGPTTheme.colors.warning};
            border-color: ${chatGPTTheme.colors.warning}30;
          `;
        case 'pending':
          return `
            background: ${chatGPTTheme.colors.textSecondary}15;
            color: ${chatGPTTheme.colors.textSecondary};
            border-color: ${chatGPTTheme.colors.textSecondary}30;
          `;
        default:
          return '';
      }
    }}
  }
`;

const HistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state: authState } = useAuth();
  const {
    chatList,
    isLoading,
    error,
    deleteChatFromLocal,
    loadChatHistoryFromServer,
  } = useChatHistoryStorage();

  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // 检查登录状态
  useEffect(() => {
    if (!authState.isAuthenticated) {
      message.warning(t('auth.requireLogin'));
      navigate('/login', { 
        replace: true,
        state: { from: location.pathname + location.search }
      });
    }
  }, [authState.isAuthenticated, navigate, t, location]);

  // 加载服务器数据
  useEffect(() => {
    if (authState.isAuthenticated) {
      loadChatHistoryFromServer(1, 20, true);
    }
  }, [authState.isAuthenticated, loadChatHistoryFromServer]);

  // 过滤历史记录
  const filteredHistory = chatList.filter(chat => {
    const matchesSearch = chat.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = !dateRange || !dateRange[0] || !dateRange[1] ||
      (dayjs(chat.timestamp).isAfter(dateRange[0]) && dayjs(chat.timestamp).isBefore(dateRange[1]));
    
    return matchesSearch && matchesDate;
  });

  // 处理聊天项点击
  const handleChatClick = (chatId: string) => {
    if (!authState.isAuthenticated) {
      const targetPath = `/chat?history=${chatId}`;
      message.warning(t('auth.requireLogin'));
      navigate('/login', { 
        replace: true,
        state: { from: targetPath }
      });
      return;
    }
    navigate(`/chat?history=${chatId}`);
  };

  // 处理删除
  const handleDelete = (chatId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    Modal.confirm({
      title: t('history.deleteConfirm.title'),
      content: t('history.deleteConfirm.content'),
      onOk: () => {
        deleteChatFromLocal(chatId);
      }
    });
  };

  // 获取状态文本
  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return '完了';
      case 'analyzing': return '分析中';
      case 'pending': return '待機中';
      default: return status;
    }
  };

  // 获取分类文本
  const getCategoryText = (category: string) => {
    switch (category) {
      case 'labor': return '労働';
      case 'traffic': return '交通';
      case 'contract': return '契約';
      default: return category;
    }
  };

  return (
    <HistoryContainer>
      <HeaderSection>
        <StyledTitle level={2}>{t('history.title')}</StyledTitle>
        
        <FilterSection>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder={t('history.search.placeholder')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <RangePicker
                style={{ width: '100%' }}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
                placeholder={[t('history.dateRange.start'), t('history.dateRange.end')]}
              />
            </Col>
          </Row>
        </FilterSection>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>{t('common.loading')}</Text>
          </div>
        ) : filteredHistory.length === 0 ? (
          <Empty
            description={t('history.empty')}
            style={{ margin: '40px 0' }}
          />
        ) : (
          <List
            dataSource={filteredHistory}
            renderItem={chat => (
              <HistoryCard onClick={() => handleChatClick(chat.id)}>
                <Row justify="space-between" align="middle">
                  <Col flex="1">
                    <Title level={5} style={{ margin: 0 }}>{chat.title}</Title>
                    <Space size="small" style={{ marginTop: '8px' }}>
                      <Tag icon={<MessageOutlined />}>
                        {chat.messages.length} {t('history.messages')}
                      </Tag>
                      <Text type="secondary">
                        {dayjs(chat.timestamp).format('YYYY-MM-DD HH:mm')}
                      </Text>
                    </Space>
                  </Col>
                  <Col>
                    <Space>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={(e) => handleDelete(chat.id, e)}
                      />
                    </Space>
                  </Col>
                </Row>
              </HistoryCard>
            )}
          />
        )}
      </HeaderSection>
    </HistoryContainer>
  );
};

export default HistoryPage;
